/**
 * API Response Types for Kampus Unified Frontend
 * Based on backend endpoint responses
 */

// ============================================
// Auth Types
// ============================================

export type UserRole = 
  | 'admin'      // Legacy, maps to k-admin
  | 'client'     // Legacy, maps to c-client
  | 'service'    // Service accounts
  | 'k-admin'    // Kampus admin
  | 'k-csteam'   // Kampus customer service
  | 'c-owner'    // Client org owner
  | 'c-client'   // Client standard user
  | 'c-reader';  // Client read-only

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  organizationId: string | null;
  username?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

export interface AuthMeResponse {
  user: AuthUser;
  tokenExpiringSoon?: boolean;
}

export interface RefreshResponse {
  token?: string;
  user?: AuthUser;
  ok?: boolean;
  message?: string;
}

export interface VerifyResponse {
  valid: boolean;
  reason?: string;
  expiringSoon?: boolean;
  expiresAt?: string;
}

export interface LogoutResponse {
  ok: boolean;
  message?: string;
}

// ============================================
// TTS Pipeline Types
// ============================================

/**
 * TTS Job Status - includes pause/stop states
 */
export type TTSJobStatus = 
  | 'pending' 
  | 'running' 
  | 'processing' 
  | 'paused' 
  | 'stopped' 
  | 'completed' 
  | 'failed';

/**
 * Checkpoint identifiers for pause points in the TTS pipeline
 */
export type CheckpointId = 
  | 'before_tts_synthesis' 
  | 'before_credits' 
  | 'before_ocr';

/**
 * Checkpoint cost level
 */
export type CheckpointCostLevel = 'low' | 'medium' | 'high';

/**
 * Checkpoint definition
 */
export interface Checkpoint {
  id: CheckpointId;
  label: string;
  step: string;
  cost_level: CheckpointCostLevel;
  description?: string;
  estimated_cost?: number;
}

/**
 * TTS Pipeline checkpoints - pause points before costly operations
 */
export const TTS_CHECKPOINTS: Checkpoint[] = [
  {
    id: 'before_tts_synthesis',
    label: 'TTS Synthesis',
    step: 'synthesize_tts',
    cost_level: 'high',
    description: 'ElevenLabs per-chunk charges',
  },
  {
    id: 'before_credits',
    label: 'Credits Generation',
    step: 'generate_credits',
    cost_level: 'medium',
    description: 'Additional ElevenLabs cost',
  },
  {
    id: 'before_ocr',
    label: 'OCR Processing',
    step: 'image_processing',
    cost_level: 'medium',
    description: 'GPT Vision costs',
  },
];

export interface TTSJobMetadata {
  job_id: string;
  pdf_path: string;
  tenant_id: string;
  priority: number;
  voice_id: string;
  status: string;
  created_at: number;
  updated_at: number;
  worker_id: string | null;
  error_message: string | null;
  progress: number;
  chunk_size?: number;
  quality?: string;
  skip_credits?: boolean;
  dry_run?: boolean;
  isbn?: string;
}

export interface TTSJob {
  id: number;
  job_id: string;
  pdf_path: string | null;
  tenant_id: string | null;
  priority: number;
  voice_id: string | null;
  chunk_size: number;
  status: TTSJobStatus;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  worker_id: string | null;
  error_message: string | null;
  progress: number;
  metadata: string; // JSON string of TTSJobMetadata
  status_message: string;
  // Pause/Stop related fields
  pause_requested?: boolean;
  stop_requested?: boolean;
  paused_at_checkpoint?: CheckpointId | null;
  paused_at?: string | null;
  stopped_at?: string | null;
  stop_reason?: string | null;
}

export interface QueueStats {
  pending_jobs: number;
  processing_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  total_workers: number;
  active_workers: number;
}

export interface JobMetrics {
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  success_rate: number;
  average_processing_time: number;
}

export interface CombinedStats {
  queue_stats: QueueStats;
  job_metrics: JobMetrics;
  system_status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  backend_type: string;
}

export interface TelemetryRun {
  id: string;
  run_id: string;
  status: 'completed' | 'processing' | 'pending' | 'failed';
  duration_ms: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface TelemetrySummary {
  event_counts: Record<string, number>;
  extract_structure: TelemetryStage;
  synthesize_tts: TelemetryStage;
  assemble_audio: TelemetryStage;
  generate_credits: TelemetryStage;
  qa_finalize: TelemetryStage;
}

export interface TelemetryStage {
  started: string | null;
  completed: string | null;
  status: 'not_started' | 'in_progress' | 'completed' | 'failed';
}

export interface TelemetryResponse {
  run_id: string;
  total_events: number;
  events: TelemetryEvent[];
  summary: TelemetrySummary;
}

export interface TelemetryEvent {
  id: number;
  job_id: string;
  event_type: string;
  timestamp: string;
  payload: string;
}

// ============================================
// TTS Telemetry - Last Run / Timeline Types
// (worker /telemetry/last and /telemetry/{runId})
// ============================================

/**
 * Event shape returned by the TTS worker telemetry endpoints
 * (/telemetry/last, /telemetry/{runId})
 */
export interface LastRunTelemetryEvent {
  event_name: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Per-stage summary object in the worker telemetry summary.
 * We keep this loose and normalize status in the UI layer.
 */
export interface LastRunTelemetryStage {
  started?: string | null;
  completed?: string | null;
  status?: string;
  // Additional fields are allowed depending on the stage
  [key: string]: unknown;
}

/**
 * Summary block from /telemetry/last:
 * - event_counts: histogram of event types
 * - stage keys like pdf_ingestion, image_processing, chapter_detection, synthesize_tts, assemble_audio, generate_credits, qa_finalize
 */
export interface LastRunTelemetrySummary {
  event_counts: Record<string, number>;
  // Stage summaries keyed by stage name
  [stageKey: string]: unknown;
}

/**
 * Response from /telemetry/last (proxied as /tts/telemetry/last)
 * and /telemetry/{runId} (proxied as /tts/telemetry/timeline/:runId).
 */
export interface LastRunTelemetryResponse {
  run_id?: string;
  total_events?: number;
  events?: LastRunTelemetryEvent[];
  summary?: LastRunTelemetrySummary;
  status?: string;
  last_event?: string | null;
  error?: string;
}

// ============================================
// GPT/GROK Concurrency Analysis Types
// (/telemetry/gpt-concurrency/{runId})
// ============================================

export interface GptConcurrencyStart {
  timestamp_est?: string;
  timestamp?: string;
  [key: string]: unknown;
}

export interface GptConcurrencyAnalysis {
  // Raw starts list from worker (optional)
  starts?: Array<GptConcurrencyStart | string>;
  // Precomputed timeline buckets (optional)
  time_labels?: string[];
  concurrency_data?: number[];
  // Summary metrics
  total_api_calls?: number;
  max_concurrent_calls?: number;
  avg_concurrency?: number;
  efficiency_score?: number;
  // Optional metadata
  min_time?: string | Date;
  max_time?: string | Date;
  slice_seconds?: number;
  [key: string]: unknown;
}

// ============================================
// Telemetry Step Averages
// (/tts/telemetry/step-averages)
// ============================================

export interface StepTimingAverage {
  key: string;
  label: string;
  avg_duration_ms: number;
  avg_duration_minutes: number;
  sample_count: number;
}

export interface StepTimingsAveragesResponse {
  window_days: number;
  runs_considered: number;
  steps: StepTimingAverage[];
}

// ============================================
// Distribution Types
// ============================================

export type BookStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'INGESTED'
  | 'TRANSFORMED'
  | 'VALIDATED'
  | 'PUBLISHED'
  | 'FEED_UPDATED'
  | 'PUSHED'
  | 'PULLED_CONFIRMED'
  | 'FAILED'
  | 'CANCELLED'
  | 'ERROR';

export interface Book {
  id: string;
  isbn: string;
  title: string;
  language?: string;
  status: BookStatus | null;
  version: number;
  error_code?: string | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  publisher_id?: string | null;
  publisher_name?: string | null;
  distributor_id?: string | null;
  distributor_name?: string | null;
  organization_id?: string | null;
  cover_key?: string | null;
  subtitle?: string | null;
  contributors_json?: Record<string, unknown>;
}

export interface BooksListResponse {
  items: Book[];
  page: number;
  pageSize: number;
  total: number;
  filters?: Record<string, unknown>;
}

export interface Publisher {
  id: string;
  name: string;
  code?: string;
  pub_name?: string;
  connector_type?: string;
  mode?: 'push' | 'pull' | 'hybrid';
  active?: boolean;
  created_at?: string | null;
  // Stats fields (computed/aggregated)
  books_count?: number;
  published_count?: number;
  pending_count?: number;
  failed_count?: number;
}

export interface PublishersListResponse {
  items: Publisher[];
  page: number;
  pageSize: number;
  total: number;
}

export interface Platform {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'inactive';
}

export interface Distributor {
  id: string;
  code: string;
  distributor_name: string;
  name?: string; // alias for distributor_name
  connector_type: string;
  mode: 'push' | 'pull' | 'hybrid';
  confirmation_method?: 'webhook' | 'poll' | 'none';
  pull_sla_hours?: number | null;
  active?: boolean;
  config_json?: Record<string, unknown>;
  created_at?: string | null;
  // Stats fields (computed/aggregated)
  books_sent?: number; // from backend - count of succeeded connector jobs
  books_count?: number; // legacy alias
  published_count?: number;
  pending_count?: number;
  failed_count?: number;
}

// Connector job types
export type ConnectorJobType = 'push' | 'push_dry_run' | 'feed_rebuild';
export type ConnectorJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface ConnectorJob {
  id: string;
  delivery_id: string;
  distributor_id: string;
  connector_job_type: ConnectorJobType;
  connector_job_status: ConnectorJobStatus;
  attempt_count: number;
  last_error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ConnectorJobsListResponse {
  items: ConnectorJob[];
  page: number;
  pageSize: number;
  total: number;
  filters?: {
    status?: string;
    distributor_id?: string;
  };
}

export interface DistributorsListResponse {
  items: Distributor[];
  page: number;
  pageSize: number;
  total: number;
}

export interface DistributorPublishedCount {
  distributor_id: string;
  distributor_name?: string;
  published_count: number;
}

// Distribution Worker: /admin/distributors/published-counts (proxied via backend)
export type DistributorsPublishedCountsResponse = DistributorPublishedCount[];

// Distribution pipeline status (per-ISBN) from Distribution Worker
export type DistributionPipelineStep =
  | 'unzipping'
  | 'Staging copied'
  | 'Onix generation'
  | 'Publish onix'
  | 'validate onix';

export interface DistributionStatusResponse {
  // Worker returns arbitrary keys, but UI focuses on the known pipeline steps
  [stepName: string]: string | undefined;
}

// Detailed distribution pipeline report (per-ISBN) from Distribution Worker
export interface DistributionReportResponse {
  isbn: string;
  publisher?: string;
  unzipping_start?: string;
  unzipping_end?: string;
  unzipping?: string;
  staging_copy_start?: string;
  staging_copy_end?: string;
  'Staging copied'?: string;
  onix_generation_start?: string;
  onix_generation_end?: string;
  'Onix generation'?: string;
  publish_onix_start?: string;
  publish_onix_end?: string;
  'Publish onix'?: string;
  validate_onix_start?: string;
  validate_onix_end?: string;
  [key: string]: unknown;
}

export const DISTRIBUTION_PIPELINE_STEPS: DistributionPipelineStep[] = [
  'unzipping',
  'Staging copied',
  'Onix generation',
  'Publish onix',
  'validate onix',
];

// ============================================
// SFTP Connector Types
// ============================================

export type SFTPTransferStatus = 
  | 'pending'
  | 'transferring'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface SFTPTransfer {
  id: string;
  isbn: string;
  publisher_id?: string;
  publisher_name?: string;
  book_title?: string;
  destination_host: string;
  destination_path: string;
  source_bucket?: string;
  source_key?: string;
  status: SFTPTransferStatus;
  priority: number;
  bytes_total?: number;
  bytes_transferred?: number;
  transfer_rate?: number; // bytes per second
  error_message?: string | null;
  error_code?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at?: string;
  metadata?: Record<string, unknown>;
}

export interface SFTPTransfersListResponse {
  items: SFTPTransfer[];
  page: number;
  pageSize: number;
  total: number;
  filters?: Record<string, unknown>;
}

export interface SFTPConnection {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string;
  base_path?: string;
  active: boolean;
  last_test_at?: string | null;
  last_test_status?: 'success' | 'failed' | null;
  last_test_error?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface SFTPConnectionsListResponse {
  items: SFTPConnection[];
  total: number;
}

export interface SFTPStats {
  total_transfers: number;
  pending_transfers: number;
  transferring_count: number;
  completed_today: number;
  failed_today: number;
  total_bytes_transferred: number;
  avg_transfer_rate: number;
  queue_depth: number;
}

export interface SFTPTransferLog {
  id: string;
  transfer_id: string;
  event_type: string;
  message: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface SFTPTransferLogsResponse {
  transfer_id: string;
  items: SFTPTransferLog[];
  total: number;
}

export interface SFTPQueueStatus {
  queue_size: number;
  active_transfers: number;
  max_concurrent: number;
  paused: boolean;
  next_transfer_at?: string | null;
}

// ============================================
// Admin User Types
// ============================================

export interface AdminUser {
  id: string;
  email: string;
  username?: string;
  role: UserRole;
  organization_id?: string | null;
  organization_name?: string | null;
  status?: 'active' | 'inactive';
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UsersListResponse {
  items: AdminUser[];
  page: number;
  pageSize: number;
  total: number;
}

// ============================================
// Admin Organization Types
// ============================================

export interface Organization {
  id: string;
  name: string;
  external_ref?: string | null;
  created_at?: string | null;
  // Computed/aggregated fields
  publishers_count?: number;
  users_count?: number;
}

export interface OrganizationsListResponse {
  items: Organization[];
  page: number;
  pageSize: number;
  total: number;
}

// ============================================
// Health & System Types
// ============================================

export type HealthStatus = 'ok' | 'degraded';
export type HealthCheckValue = 'ok' | 'error' | 'unknown';

export interface HealthChecks {
  api: HealthCheckValue;
  tts_worker: HealthCheckValue;
  distribution_worker: HealthCheckValue;
  db: HealthCheckValue;
}

export interface HealthCheck {
  status: HealthStatus;
  checks: HealthChecks;
  timestamp: string;
}

// --------------------------------------------
// TTS Worker Health Types (/tts/health)
// --------------------------------------------

export interface TTSWorkerR2Health {
  status: string;
  bucket?: string;
  objects_count?: number;
  error?: string | null;
}

export interface TTSWorkerD1Health {
  status: string;
  database?: string;
  error?: string | null;
}

export interface TTSWorkerKVHealth {
  status: string;
  namespaces?: string[];
  error?: string | null;
}

export interface TTSWorkerServicesHealth {
  r2_storage?: TTSWorkerR2Health;
  d1_database?: TTSWorkerD1Health;
  kv_storage?: TTSWorkerKVHealth;
}

export interface TTSWorkerSystemInfo {
  worker_name?: string;
  environment?: string;
  region?: string;
  runtime?: string;
}

export interface TTSWorkerHealth {
  status: string;
  message?: string;
  timestamp: string;
  version?: string;
  build_timestamp?: string;
  deployment_id?: string;
  health_check_duration_ms?: number;
  services?: TTSWorkerServicesHealth;
  system_info?: TTSWorkerSystemInfo;
}

// --------------------------------------------
// TTS Synthesizer Lambda Health (/tts/elevenlabs/health)
// --------------------------------------------

export interface ElevenLabsDependencyHealth {
  status: string;
  status_code?: number;
  response_time_ms?: number;
  endpoint?: string;
}

export interface TTSSynthesizerHealth {
  status: string;
  function: string;
  version?: string;
  git_sha?: string;
  build_time?: string;
  time?: string;
  function_version?: string;
  request_id?: string;
  dependencies?: {
    elevenlabs?: ElevenLabsDependencyHealth;
    // Allow other dependencies without strict typing
    [key: string]: unknown;
  };
}

// ============================================
// TTS Job Control Response Types (Pause/Resume/Stop)
// ============================================

/**
 * Response from POST /tts/jobs/:jobId/pause
 */
export interface PauseJobResponse {
  success: boolean;
  job_id: string;
  message: string;
  will_pause_at: CheckpointId;
}

/**
 * Response from POST /tts/jobs/:jobId/resume
 */
export interface ResumeJobResponse {
  success: boolean;
  job_id: string;
  message: string;
  resumed_from: CheckpointId;
}

/**
 * Response from POST /tts/jobs/:jobId/stop
 */
export interface StopJobResponse {
  success: boolean;
  job_id: string;
  message: string;
  stopped_at_step: string;
  partial_outputs: string[];
}

/**
 * Response from POST /tts/jobs/:jobId/restart
 */
export interface RestartJobResponse {
  success: boolean;
  job_id: string;
  message: string;
  previous_status: string;
  artifacts_deleted: number;
  restart_count: number;
  trigger_result?: {
    step: string;
    accepted?: boolean;
    queued?: boolean;
    error?: string;
  };
}

/**
 * Response from GET /tts/jobs/:jobId/checkpoints
 */
export interface CheckpointsResponse {
  job_id: string;
  available_checkpoints: Checkpoint[];
  next_checkpoint: Checkpoint | null;
  current_step: string | null;
  estimated_remaining_cost?: number;
  cost_spent?: number;
}

/**
 * Extended job status response with pause/stop fields
 */
export interface JobStatusWithPauseStop {
  success: boolean;
  job_id: string;
  status: TTSJobStatus;
  last_event: string | null;
  current_step: string | null;
  last_completed_step: string | null;
  next_step: string | null;
  is_stuck: boolean;
  stuck_reason: string | null;
  events_count: number;
  last_activity: string | null;
  suggested_retry_step: string | null;
  pipeline_progress: {
    completed_steps: number;
    total_steps: number;
    percentage: number;
    step_details: Record<string, {
      started: boolean;
      completed: boolean;
      startTime: string | null;
      endTime: string | null;
    }>;
  };
  timestamp: string;
  // Pause/Stop fields
  pause_requested?: boolean;
  stop_requested?: boolean;
  paused_at_checkpoint?: CheckpointId | null;
  paused_at?: string | null;
  stopped_at?: string | null;
  stop_reason?: string | null;
  // Checkpoint info
  available_checkpoints?: Checkpoint[];
  next_checkpoint?: Checkpoint | null;
  // Cost info
  estimated_remaining_cost?: number;
  cost_spent?: number;
}

// ============================================
// API Response Wrappers
// ============================================

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: string;
  message?: string;
  details?: unknown;
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// ============================================
// Helper Functions
// ============================================

/**
 * Parse TTSJob metadata JSON string
 */
export function parseJobMetadata(job: TTSJob): TTSJobMetadata | null {
  try {
    return JSON.parse(job.metadata) as TTSJobMetadata;
  } catch {
    return null;
  }
}

export function getDisplayTitleFromPdfPath(pdfPath: string | null | undefined): string | null {
  const fileName = pdfPath?.split('/').pop();
  if (!fileName) return null;

  try {
    return decodeURIComponent(fileName)
      .replace(/\.pdf$/i, '')
      .replace(/-/g, ' ');
  } catch {
    return fileName.replace(/\.pdf$/i, '').replace(/-/g, ' ');
  }
}

/**
 * Get title from job - checks direct field first, then metadata
 */
export function getJobTitle(job: TTSJob): string {
  // Check direct pdf_path field first (new API format)
  const directTitle = getDisplayTitleFromPdfPath(job.pdf_path);
  if (directTitle) return directTitle;

  // Fallback to metadata JSON (legacy format)
  const metadata = parseJobMetadata(job);
  return getDisplayTitleFromPdfPath(metadata?.pdf_path) || job.job_id;
}

/**
 * Get voice from job - checks direct field first, then metadata
 */
export function getJobVoice(job: TTSJob): string {
  // Check direct fields first (new API format)
  // Use type assertion via unknown for fields that may exist at runtime but not in type
  const jobAny = job as unknown as { voice?: string; voice_name?: string };
  const directVoice = jobAny.voice || jobAny.voice_name || job.voice_id;
  if (directVoice && typeof directVoice === 'string') {
    return directVoice;
  }
  // Fallback to metadata JSON (legacy format)
  const metadata = parseJobMetadata(job);
  return metadata?.voice_id || 'Unknown';
}

/**
 * Get ISBN from job metadata
 */
export function getJobIsbn(job: TTSJob): string | null {
  const metadata = parseJobMetadata(job);
  return metadata?.isbn || null;
}

/**
 * Format duration from milliseconds
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
